import React from "react";
import { isMobile, whichBrowser } from "src/utils/browser";
import { getLanguageField } from "src/utils/language";
import { getCurrentCurrency, getCurrentUnits, setCurrentCurrency, setCurrentUnits } from "src/utils/localStorage";

interface ApplicationContextProviderProps {
  /** current destination */
  destinationId: string;
  /** default currency */
  defaultCurrency: string;
  /** current language */
  language: string;
  /** default language */
  defaultLanguage: string;
  /** is in app mode */
  isInApp: boolean;
  /** application nodes */
  children?: React.ReactNode;
}

interface ApplicationContextProviderState {
  context: IApplicationContext
}

/** Pre-props initial context: */
const initialContextValues: Partial<IApplicationContext> = {
  currency: undefined,
  defaultCurrency: undefined,
  units: undefined,
}

/**
 * Initial context.
 */
const ApplicationContext = React.createContext<IApplicationContext>(initialContextValues as any);

/**
 * Application Context Provider
 */
class ApplicationContextProvider extends React.Component<ApplicationContextProviderProps, ApplicationContextProviderState> {

  /** helper functions: */
  public helpers = {
    translate: (translations: TTranslations<any> = [], field: string, isHtml: boolean = false, mapHtml: boolean = false, htmlClass?: string) => {
      return getLanguageField(translations, this.state.context.language, field, isHtml, mapHtml, htmlClass);
    }
  }

  /** state modifiers: */
  public setters = {
    setCurrency: (newCurrency: string) => {
      const next_context = { ...this.state.context, currency: newCurrency, ...this.setters, };
      setCurrentCurrency(newCurrency);
      this.setState({context: next_context})
    },
    setUnitSystem: (newUnitSystem: "metric" | "imperial") => {
      const next_context = { ...this.state.context, units: newUnitSystem, ...this.setters, };
      setCurrentUnits(newUnitSystem);
      this.setState({context: next_context});
    },
  }

  /** initial state */
  public state: ApplicationContextProviderState = {
    context: {
      ready: false,
      destinationId: this.props.destinationId,
      language: this.props.language,
      defaultLanguage: this.props.defaultLanguage,
      currency: getCurrentCurrency(this.props.defaultCurrency),
      defaultCurrency: this.props.defaultCurrency,
      units: getCurrentUnits(undefined),
      browser: 'unknown',
      isMobile: false,
      isInApp: this.props.isInApp,
      ...this.setters,
      ...this.helpers,
    }
  }

  /** Populate context values with run-time values: */
  componentDidMount(): void {
    this.setState({
      context: {
        ...this.state.context,
        ready: true,
        browser: whichBrowser(),
        isMobile: isMobile(),
      }
    });
  }

  /** render provider */
  render() {
    return (
      <ApplicationContext.Provider value={this.state.context}>
        {this.props.children}
      </ApplicationContext.Provider>
    );
  }
}


export { ApplicationContextProvider as default, ApplicationContextProvider, ApplicationContext }
